import { useRef, useState } from "react";
import axios from "axios";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2, Upload } from "lucide-react";
import { API } from "@/lib/content";

export const LABELS = {
  name: "Nombre", desc: "Descripción", cap: "Nombre de la foto", link: "Vincular a platillo", title: "Título", de: "Subtítulo (alemán)",
  note: "Nota", text: "Texto", img: "Foto", src: "Foto", items: "Elementos", groups: "Grupos", images: "Fotos", address: "Dirección",
  tel: "Teléfono", art: "Ilustración", map: "Enlace del mapa", value: "Número", label: "Etiqueta", prefix: "Prefijo", sub: "Subtítulo", cta: "Texto del botón",
};
const HIDDEN = new Set(["key", "span", "target", "whatsapp", "id"]);
const IMG_KEYS = new Set(["img", "src", "hero_photo", "pas_catalog_img", "eco_photo", "art"]);
const TEMPLATES = { images: { src: "", cap: "", link: "" }, items: { name: "", desc: "" }, groups: { title: "Nuevo grupo", items: [] } };

const blank = (v) => {
  if (typeof v === "string") return "";
  if (typeof v === "number") return 0;
  if (Array.isArray(v)) return [];
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k === "key" ? `k${Date.now().toString(36)}` : blank(x)]));
  return "";
};

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
  return `${API.replace(/\/api$/, "")}${res.data.url}`;
};

const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr("");
    try {
      onChange(await uploadImage(f));
    } catch (x) {
      setErr(x.response?.data?.detail?.toString?.() || "No se pudo subir la imagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gold/20 bg-espresso">{value && <img src={value} alt="" className="h-full w-full object-cover" />}</div>
      <div className="min-w-0 flex-1 space-y-1">
        <button type="button" data-testid={`${testid}-upload`} onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-3 py-1.5 text-xs text-cream hover:bg-gold hover:text-espresso disabled:opacity-50">
          <Upload size={13} /> {busy ? "Subiendo…" : value ? "Cambiar foto" : "Subir foto"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
        {err && <p className="text-xs text-red-300">{err}</p>}
      </div>
    </div>
  );
};

const inputCls = "w-full rounded-lg border border-gold/20 bg-espresso px-3 py-2 text-sm text-cream outline-none focus:border-gold";

export const Field = ({ k, value, onChange, path, linkOptions, imageList }) => {
  const testid = `edit-${path.replace(/[^a-zA-Z0-9]+/g, "-")}`;
  if (k === "link" && linkOptions) {
    return (
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">— Usar el nombre de la foto —</option>
        {linkOptions.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (IMG_KEYS.has(k) || imageList) return <ImageField value={value} onChange={onChange} testid={testid} />;
  if (typeof value === "number") return <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className={inputCls} />;
  if (String(value).length > 70) return <textarea data-testid={testid} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  return <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
};

const R = {};

const ArrayEditor = ({ k, value, onChange, path, linkOptions }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const set = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
  const move = (i, d) => {
    const n = [...value];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  const add = () => onChange([...value, value.length ? blank(value[0]) : TEMPLATES[k] ?? ""]);
  const isObj = value.some((x) => x && typeof x === "object");
  return (
    <div className="space-y-2">
      {value.map((item, i) => {
        const title = isObj ? item.name || item.title || item.cap || item.label || `#${i + 1}` : null;
        return (
          <div key={i} className="rounded-xl border border-gold/15 bg-espresso/60 p-2">
            <div className="flex items-center gap-2">
              {isObj ? (
                <button type="button" onClick={() => setOpenIdx(openIdx === i ? null : i)} className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-cream">
                  <ChevronDown size={14} className={`shrink-0 transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
                  {(item.img || item.src) && <img src={item.img || item.src} alt="" className="h-7 w-7 rounded object-cover" />}
                  <span className="truncate">{title}</span>
                </button>
              ) : (
                <div className="flex-1">
                  <Field k={k} value={item} onChange={(v) => set(i, v)} path={`${path}.${i}`} imageList={k === "images"} />
                </div>
              )}
              <button type="button" aria-label="Subir" onClick={() => move(i, -1)} className="p-1 text-sand hover:text-gold"><ArrowUp size={14} /></button>
              <button type="button" aria-label="Bajar" onClick={() => move(i, 1)} className="p-1 text-sand hover:text-gold"><ArrowDown size={14} /></button>
              <button type="button" data-testid={`edit-del-${path.replace(/[^a-zA-Z0-9]+/g, "-")}-${i}`} aria-label="Eliminar" onClick={() => window.confirm("¿Eliminar este elemento?") && onChange(value.filter((_, j) => j !== i))} className="p-1 text-sand hover:text-red-300"><Trash2 size={14} /></button>
            </div>
            {isObj && openIdx === i && (
              <div className="mt-3 border-t border-gold/10 pt-3">
                <R.Obj value={item} onChange={(v) => set(i, v)} path={`${path}.${i}`} linkOptions={linkOptions} />
              </div>
            )}
          </div>
        );
      })}
      <button type="button" data-testid={`edit-add-${path.replace(/[^a-zA-Z0-9]+/g, "-")}`} onClick={add} className="inline-flex items-center gap-1 rounded-full border border-dashed border-gold/40 px-3 py-1.5 text-xs text-gold hover:bg-gold/10">
        <Plus size={13} /> Agregar
      </button>
    </div>
  );
};

export const ObjectEditor = ({ value, onChange, path = "root", linkOptions, labels = {} }) => (
  <div className="space-y-4">
    {Object.entries(value || {})
      .filter(([k]) => !HIDDEN.has(k))
      .map(([k, v]) => (
        <div key={k}>
          <p className="mb-1.5 text-[11px] uppercase tracking-[0.2em] text-gold">{labels[k] || LABELS[k] || k}</p>
          {Array.isArray(v) ? (
            <ArrayEditor k={k} value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} path={`${path}.${k}`} linkOptions={linkOptions} />
          ) : v && typeof v === "object" ? (
            <div className="rounded-xl border border-gold/10 p-3">
              <R.Obj value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} path={`${path}.${k}`} linkOptions={linkOptions} />
            </div>
          ) : (
            <Field k={k} value={v ?? ""} onChange={(nv) => onChange({ ...value, [k]: nv })} path={`${path}.${k}`} linkOptions={linkOptions} />
          )}
        </div>
      ))}
  </div>
);
R.Obj = ObjectEditor;

export const AnyEditor = ({ value, onChange, path, linkOptions }) =>
  Array.isArray(value) ? (
    <ArrayEditor k={path} value={value} onChange={onChange} path={path} linkOptions={linkOptions} />
  ) : (
    <ObjectEditor value={value} onChange={onChange} path={path} linkOptions={linkOptions} />
  );
